import { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Check, ChevronsUpDown, Plus, X } from "lucide-react";

// Searchable multi/single select bound to a pool. Supports create-on-the-fly.
export default function TagPicker({ value, onChange, options = [], onCreate, multi = false, placeholder = "Seç veya ara...", testid }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const selected = multi ? (Array.isArray(value) ? value : []) : (value ? [value] : []);

  const toggle = (v) => {
    if (multi) {
      const next = selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v];
      onChange(next);
    } else {
      onChange(v === value ? "" : v);
      setOpen(false);
    }
    setQuery("");
  };

  const create = async () => {
    const v = query.trim();
    if (!v) return;
    if (onCreate) await onCreate(v);
    if (multi) onChange([...selected.filter((x) => x !== v), v]);
    else { onChange(v); setOpen(false); }
    setQuery("");
  };

  const exists = options.some((o) => o.toLowerCase() === query.trim().toLowerCase());

  return (
    <div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" role="combobox" data-testid={testid}
            className="h-auto min-h-11 w-full justify-between rounded-xl px-3 py-2 font-normal">
            <span className="flex flex-1 flex-wrap gap-1.5">
              {selected.length === 0 && <span className="text-slate-400">{placeholder}</span>}
              {multi
                ? selected.map((s) => (
                    <span key={s} className="inline-flex items-center gap-1 rounded-md bg-violet-100 px-2 py-0.5 text-xs font-medium text-violet-700">
                      {s}
                      <span role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); toggle(s); }} className="cursor-pointer rounded hover:bg-violet-200"><X className="h-3 w-3" /></span>
                    </span>
                  ))
                : selected.map((s) => <span key={s} className="text-slate-800">{s}</span>)}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <Command>
            <CommandInput placeholder="Ara..." value={query} onValueChange={setQuery} data-testid={testid ? `${testid}-search` : undefined} />
            <CommandList>
              <CommandEmpty>
                {query.trim() ? (
                  <button type="button" onClick={create} className="flex w-full items-center gap-2 px-3 py-2 text-sm text-violet-600 hover:bg-violet-50" data-testid={testid ? `${testid}-create` : undefined}>
                    <Plus className="h-4 w-4" /> “{query.trim()}” ekle
                  </button>
                ) : "Sonuç yok."}
              </CommandEmpty>
              <CommandGroup>
                {options.map((o) => (
                  <CommandItem key={o} value={o} onSelect={() => toggle(o)} data-testid={testid ? `${testid}-opt-${o}` : undefined}>
                    <Check className={`mr-2 h-4 w-4 ${selected.includes(o) ? "opacity-100" : "opacity-0"}`} />
                    {o}
                  </CommandItem>
                ))}
                {query.trim() && !exists && (
                  <CommandItem value={`__create_${query}`} onSelect={create} className="text-violet-600">
                    <Plus className="mr-2 h-4 w-4" /> “{query.trim()}” ekle
                  </CommandItem>
                )}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
